import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem,
  TextField, Typography,
} from '@mui/material';
import { requirementsApi } from '../../services/api';

/**
 * #1084: close a change request as rejected, withdrawn or superseded, with a reason.
 *
 * A human does this in the app. An MCP agent can only PROPOSE a closure: the backend
 * stores the proposal in `attributes.cr_close_proposed`. This panel shows the proposal
 * with Confirm and Dismiss buttons. On a resolved change request, the panel shows the
 * outcome, the reason, who closed it and when.
 *
 * The server validates the reason and `superseded_by` again. The checks here only tell
 * the user before the round trip.
 */

export const CR_RESOLVED_STATES = ['verified', 'closed', 'rejected', 'withdrawn', 'superseded'];

export const CLOSURE_OUTCOMES = [
  { value: 'rejected', label: 'Reject', past: 'Rejected' },
  { value: 'withdrawn', label: 'Withdraw', past: 'Withdrawn' },
  { value: 'superseded', label: 'Supersede', past: 'Superseded' },
];

const outcomeLabel = (value) => (CLOSURE_OUTCOMES.find((o) => o.value === value)?.past
  || (value ? value.charAt(0).toUpperCase() + value.slice(1) : ''));

const formatWhen = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? String(iso) : d.toLocaleString('en-GB');
};

export function CloseChangeRequestDialog({ open, initial, busy, onCancel, onConfirm }) {
  const [status, setStatus] = useState('rejected');
  const [reason, setReason] = useState('');
  const [supersededBy, setSupersededBy] = useState('');
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    if (open) {
      setStatus(initial?.status || 'rejected');
      setReason(initial?.reason || '');
      setSupersededBy(initial?.superseded_by || '');
      setTouched(false);
    }
  }, [open, initial]);

  const reasonEmpty = reason.trim().length === 0;
  const successorEmpty = status === 'superseded' && supersededBy.trim().length === 0;
  const invalid = reasonEmpty || successorEmpty;

  const submit = () => {
    setTouched(true);
    if (invalid) return;
    onConfirm({
      status,
      reason: reason.trim(),
      supersededBy: status === 'superseded' ? supersededBy.trim() : undefined,
    });
  };

  return (
    <Dialog open={open} onClose={onCancel} maxWidth="sm" fullWidth
            data-testid="close-cr-dialog">
      <DialogTitle>Close change request</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ mb: 1.5, color: 'text.secondary' }}>
          The change request stays in the project. The outcome, the reason, your name and
          the time go into its history.
        </Typography>
        <TextField
          select
          fullWidth
          label="Outcome"
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          sx={{ mb: 2 }}
          slotProps={{ htmlInput: { 'data-testid': 'close-cr-outcome' } }}
        >
          {CLOSURE_OUTCOMES.map((o) => (
            <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
          ))}
        </TextField>
        {status === 'superseded' && (
          <TextField
            fullWidth
            label="Superseded by (change request id)"
            placeholder="REQ-0961"
            value={supersededBy}
            onChange={(e) => setSupersededBy(e.target.value)}
            error={touched && successorEmpty}
            helperText={touched && successorEmpty
              ? 'Give the id of the change request that replaces this one.' : ' '}
            sx={{ mb: 1 }}
            slotProps={{ htmlInput: { 'data-testid': 'close-cr-superseded-by' } }}
          />
        )}
        <TextField
          multiline
          minRows={3}
          fullWidth
          label="Reason"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          onBlur={() => setTouched(true)}
          error={touched && reasonEmpty}
          helperText={touched && reasonEmpty ? 'A reason is required.' : ' '}
          slotProps={{ htmlInput: { 'data-testid': 'close-cr-reason' } }}
        />
      </DialogContent>
      <DialogActions>
        <Box sx={{ flex: 1 }} />
        <Button onClick={onCancel} data-testid="close-cr-cancel">Cancel</Button>
        <Button
          variant="contained"
          color="error"
          onClick={submit}
          disabled={invalid || busy}
          data-testid="close-cr-confirm"
        >
          {CLOSURE_OUTCOMES.find((o) => o.value === status)?.label || 'Close'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

CloseChangeRequestDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  initial: PropTypes.shape({
    status: PropTypes.string,
    reason: PropTypes.string,
    superseded_by: PropTypes.string,
  }),
  busy: PropTypes.bool,
  onCancel: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
};

export default function ChangeRequestClosurePanel({ item, onItemUpdated, onError }) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogInitial, setDialogInitial] = useState(null);
  const [busy, setBusy] = useState(false);

  const attrs = item?.attributes || {};
  if (!item || !attrs.is_change_request) return null;
  const status = String(attrs.cr_status || 'open').toLowerCase();
  const resolved = CR_RESOLVED_STATES.includes(status);
  const proposal = attrs.cr_close_proposed && typeof attrs.cr_close_proposed === 'object'
    ? attrs.cr_close_proposed : null;

  const openDialog = (initial) => { setDialogInitial(initial); setDialogOpen(true); };

  const close = async (values) => {
    setBusy(true);
    try {
      const res = await requirementsApi.closeChangeRequest(item.id, values);
      setDialogOpen(false);
      if (res?.data) onItemUpdated(res.data);
    } catch (err) {
      onError(`Failed to close change request: ${err?.response?.data?.detail || err.message}`);
    } finally {
      setBusy(false);
    }
  };

  const dismiss = async () => {
    setBusy(true);
    try {
      const data = await requirementsApi.cancelChangeRequestClosure(item.id);
      if (data) onItemUpdated(data);
    } catch (err) {
      onError(`Failed to dismiss closure proposal: ${err?.response?.data?.detail || err.message}`);
    } finally {
      setBusy(false);
    }
  };

  if (resolved) {
    if (!attrs.cr_closure_reason) return null;
    const who = attrs.cr_closed_by_display || attrs.cr_closed_by;
    return (
      <Alert severity="info" sx={{ mb: 2 }} data-testid="cr-closed-alert">
        Change request {outcomeLabel(status).toLowerCase()}
        {who ? ` by ${who}` : ''}
        {attrs.cr_closed_at ? ` on ${formatWhen(attrs.cr_closed_at)}` : ''}
        {attrs.cr_superseded_by ? ` (superseded by ${attrs.cr_superseded_by})` : ''}
        : &ldquo;{attrs.cr_closure_reason}&rdquo;
      </Alert>
    );
  }

  return (
    <>
      {proposal ? (
        <Alert severity="warning" sx={{ mb: 2 }} data-testid="cr-closure-proposed-alert"
          action={(
            <>
              <Button color="inherit" size="small" disabled={busy}
                onClick={() => openDialog(proposal)} data-testid="confirm-cr-closure">
                Confirm
              </Button>
              <Button color="inherit" size="small" disabled={busy}
                onClick={dismiss} data-testid="dismiss-cr-closure">
                Dismiss
              </Button>
            </>
          )}>
          An AI agent proposed to close this change request as
          {' '}{outcomeLabel(proposal.status).toLowerCase()}
          {proposal.requested_by ? ` (requested by ${proposal.requested_by})` : ''}
          {proposal.reason ? `: "${proposal.reason}"` : ''}.
          It stays open until a human confirms or dismisses the proposal.
        </Alert>
      ) : (
        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
          <Button size="small" variant="outlined" color="error" disabled={busy}
            onClick={() => openDialog(null)} data-testid="open-close-cr">
            Reject / close change request
          </Button>
        </Box>
      )}
      <CloseChangeRequestDialog
        open={dialogOpen}
        initial={dialogInitial}
        busy={busy}
        onCancel={() => setDialogOpen(false)}
        onConfirm={close}
      />
    </>
  );
}

ChangeRequestClosurePanel.propTypes = {
  item: PropTypes.shape({
    id: PropTypes.string,
    attributes: PropTypes.object,
  }),
  onItemUpdated: PropTypes.func.isRequired,
  onError: PropTypes.func.isRequired,
};
